import { DateFormatEnum } from "@models/core/date-format";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { parseSafeDate } from "@app/shared/ui-lib/src/lib/hard-calendar/utils/date-utils";

export { parseSafeDate };

export function formatToApiDateTimeOffset(dateStr?: string | Date | null): string {
  if (!dateStr) return "";
  const date = parseSafeDate(dateStr);
  if (isNaN(date.getTime())) return "";
  return format(date, DateFormatEnum.FullDateAndTimeOffset);
}

export function formatDateForDisplay(
  dateStr?: string | Date | null,
  formatStr: string = "dd LLL HH:mm",
  caseUpper: boolean = false
): string {
  if (!dateStr) return "-";
  const date = parseSafeDate(dateStr);
  if (isNaN(date.getTime())) return typeof dateStr === 'string' ? dateStr : "-";
  const result = format(date, formatStr, { locale: ru });
  return caseUpper ? result.toUpperCase() : result;
}

export function formatSplitDate(dateStr?: string | Date | null): { day: string; time: string } {
  if (!dateStr) {
    return { day: "—", time: "" };
  }
  const date = parseSafeDate(dateStr);
  if (isNaN(date.getTime())) {
    return { day: "—", time: "" };
  }
  const day = format(date, "dd MMM", { locale: ru }).replace(".", "");
  const time = format(date, "HH:mm");
  return { day, time };
}

export function formatTimeField(time: string): string {
  if (time.length === 6) {
    return `${time[0]}${time[1]}:${time[2]}${time[3]}:${time[4]}${time[5]}`;
  } else if (time.length === 7) {
    return `${time[0]}${time[1]}${time[2]}:${time[3]}${time[4]}:${time[5]}${time[6]}`;
  }
  return time;
}

export function formatDateToZonedTime(
  dateString: string,
  formatStr: DateFormatEnum,
): string {
  if (!dateString) {
    return "";
  }
  const dateObject = new Date(dateString + "Z");
  return format(dateObject, formatStr);
}

export function formatDateTimeDisplay(dateStr?: string | Date | null): string {
  return formatDateForDisplay(dateStr, "dd LLL yyyy / HH:mm", true);
}

export function formatDateDisplay(dateStr?: string | Date | null): string {
  return formatDateForDisplay(dateStr, "dd LLL HH:mm", false);
}

export function formatDateOnlyDisplay(dateStr?: string | Date | null): string {
  return formatDateForDisplay(dateStr, "dd LLL", false);
}

export function formatTimeOnlyDisplay(dateStr?: string | Date | null): string {
  if (!dateStr) return "";
  const date = parseSafeDate(dateStr);
  if (isNaN(date.getTime())) return "";
  return format(date, "HH:mm");
}

export function formatToInputDateTime(dateStr?: string | Date | null): string {
  if (!dateStr) return "";
  const date = parseSafeDate(dateStr);
  if (isNaN(date.getTime())) return "";
  return format(date, DateFormatEnum.YearMonthDayAndTimePortion);
}

export function parseDateOrNull(value?: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    return new Date(year, month - 1, day, 0, 0, 0);
  }
  const normalized = raw.includes(' ') && !raw.includes('T')
    ? raw.replace(' ', 'T')
    : raw;
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
}
